import { useSyncExternalStore } from "react";

import type { BookingVisualVariantKey } from "./bookingVisualVariants";

/**
 * The visual variant of the booking root on screen, for overlays mounted
 * outside it. The root layout's `NewAppointmentModal` (the quote link opens
 * the appointment flow there) is a sibling of the page, so neither the
 * root's Ant `ConfigProvider` nor `BookingSkinTokensContext` reaches it.
 * `EditorialBookingPageRoot` claims its variant while mounted; the latest
 * claim wins, and `null` means no booking root is mounted.
 */
const claims: { key: BookingVisualVariantKey }[] = [];
const listeners = new Set<() => void>();

function current(): BookingVisualVariantKey | null {
  return claims.length ? claims[claims.length - 1].key : null;
}

function emit() {
  for (const listener of listeners) listener();
}

/** Registers a mounted root's variant; returns the release for unmount. */
export function claimActiveBookingVariant(
  key: BookingVisualVariantKey,
): () => void {
  const claim = { key };
  claims.push(claim);
  emit();
  return () => {
    const index = claims.indexOf(claim);
    if (index >= 0) claims.splice(index, 1);
    emit();
  };
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useActiveBookingVariant(): BookingVisualVariantKey | null {
  return useSyncExternalStore(subscribe, current, () => null);
}
